/*
  Third-level flyouts in the header dropdowns.

  - Hover intent: a flyout stays open briefly after the pointer leaves its item,
    and a sibling the pointer crosses while heading into the flyout does not
    take over, so a diagonal move can cross other items without closing it.
  - Placement: each time a flyout opens it is measured and opened to whichever
    side has room, flush against its item, and lifted to sit beside the
    dropdown rather than hang below it.
  - Touch: the first tap on an item with a flyout opens it; a second tap follows
    the link. Links inside dropdowns follow on the first tap.
*/
(function () {
  const INTENT_DELAY = 300;
  const AIM_RECHECK = 100;
  const OPEN_CLASS = 'is-flyout-open';
  const DROPDOWN_OPEN_CLASS = 'is-dropdown-open';
  const EDGE_CLASS = 'dropdown--edge';

  function setUp(nav) {
    if (nav.hasAttribute('data-flyouts')) return;
    nav.setAttribute('data-flyouts', '');

    let openItem = null;
    let timer = null;
    let pointerType = 'mouse';
    let wasOpenOnPress = false;

    function flyoutOf(item) {
      return item.querySelector(':scope > .dropdown--nested');
    }

    function place(item) {
      const flyout = flyoutOf(item);
      if (!flyout) return;
      ['--flyout-top', '--flyout-pad-top', '--flyout-pad-height', '--flyout-pad-width'].forEach(function (name) {
        flyout.style.removeProperty(name);
      });

      const itemBox = item.getBoundingClientRect();
      const width = flyout.offsetWidth;
      const viewportWidth = document.documentElement.clientWidth;
      const roomRight = viewportWidth - itemBox.right;
      const roomLeft = itemBox.left;
      const openLeft = width > roomRight && roomLeft > roomRight;
      flyout.classList.toggle(EDGE_CLASS, openLeft);

      // Lift a flyout that hangs below its dropdown up beside it (never above
      // the dropdown's top), and lift any flyout clear of the viewport bottom.
      const flyoutBox = flyout.getBoundingClientRect();
      const dropdownBox = item.closest('.dropdown').getBoundingClientRect();
      const roomAbove = Math.max(flyoutBox.top - dropdownBox.top, 0);
      let lift = Math.min(Math.max(flyoutBox.bottom - dropdownBox.bottom, 0), roomAbove);
      const viewportOverflow = flyoutBox.bottom - lift - window.innerHeight;
      if (viewportOverflow > 0) lift += Math.min(viewportOverflow + 8, Math.max(flyoutBox.top - lift, 0));
      if (lift > 0) flyout.style.setProperty('--flyout-top', `${-10 - lift}px`);

      // Whatever still hangs below the dropdown gets an invisible pad under the
      // dropdown, so a diagonal path to the lower links stays inside the menu.
      const below = flyoutBox.bottom - lift - dropdownBox.bottom;
      flyout.style.setProperty('--flyout-pad-top', `${flyoutBox.height - below}px`);
      flyout.style.setProperty('--flyout-pad-height', `${Math.max(below, 0)}px`);
      flyout.style.setProperty('--flyout-pad-width', `${dropdownBox.width}px`);
    }

    function open(item) {
      clearTimeout(timer);
      if (openItem && openItem !== item) close(openItem);
      item.classList.add(OPEN_CLASS);
      item.setAttribute('aria-expanded', 'true');
      openItem = item;
      place(item);
    }

    function close(item) {
      item.classList.remove(OPEN_CLASS);
      item.setAttribute('aria-expanded', 'false');
      if (openItem === item) openItem = null;
    }

    function closeAll() {
      clearTimeout(timer);
      if (openItem) close(openItem);
    }

    // Menu aim: while the pointer keeps heading into the open flyout's corner
    // triangle, a sibling it crosses does not take over.
    let pointer = null;
    let previousPointer = null;
    let lastAimCheck = null;

    // mouseenter fires before the mousemove for the same motion, so both feed it.
    function track(event) {
      if (pointer && pointer.x === event.clientX && pointer.y === event.clientY) return;
      previousPointer = pointer;
      pointer = { x: event.clientX, y: event.clientY };
    }

    nav.addEventListener('mousemove', track);

    function aimingAtOpenFlyout() {
      if (!openItem || !pointer || !previousPointer) return false;
      if (lastAimCheck && lastAimCheck.x === pointer.x && lastAimCheck.y === pointer.y) return false;

      const flyout = flyoutOf(openItem);
      const box = flyout.getBoundingClientRect();
      const toLeft = flyout.classList.contains(EDGE_CLASS);
      const mirror = point => ({ x: toLeft ? -point.x : point.x, y: point.y });
      const edgeX = toLeft ? box.right : box.left;
      const upper = mirror({ x: edgeX, y: box.top - 20 });
      const lower = mirror({ x: edgeX, y: box.bottom + 20 });
      const now = mirror(pointer);
      const before = mirror(previousPointer);
      const slope = (from, to) => (to.y - from.y) / (to.x - from.x);

      return slope(now, upper) < slope(before, upper) && slope(now, lower) > slope(before, lower);
    }

    function switchTo(item) {
      clearTimeout(timer);
      if (!openItem || openItem === item) {
        open(item);
        lastAimCheck = null;
      } else if (aimingAtOpenFlyout()) {
        lastAimCheck = pointer;
        timer = setTimeout(function () {
          if (item.matches(':hover')) switchTo(item);
        }, AIM_RECHECK);
      } else {
        open(item);
        lastAimCheck = null;
      }
    }

    nav.querySelectorAll('.dropdown__menuitem--nested').forEach(function (item) {
      item.addEventListener('mouseenter', function (event) {
        track(event);
        switchTo(item);
      });

      item.addEventListener('mouseleave', function () {
        clearTimeout(timer);
        timer = setTimeout(function () {
          if (openItem && !openItem.matches(':hover')) close(openItem);
        }, INTENT_DELAY);
      });
    });

    // The dropdown itself gets the same grace, so a path from its last item
    // down to a taller flyout can clip the dropdown's edge.
    let openTop = null;
    let topTimer = null;

    function closeTop() {
      clearTimeout(topTimer);
      if (!openTop) return;
      if (openItem && openTop.contains(openItem)) closeAll();
      openTop.classList.remove(DROPDOWN_OPEN_CLASS);
      openTop = null;
    }

    nav.querySelectorAll('.navigation__menuitem--dropdown').forEach(function (topItem) {
      topItem.addEventListener('mouseenter', function () {
        if (openTop !== topItem) closeTop();
        clearTimeout(topTimer);
        topItem.classList.add(DROPDOWN_OPEN_CLASS);
        openTop = topItem;
      });

      topItem.addEventListener('mouseleave', function () {
        clearTimeout(topTimer);
        topTimer = setTimeout(closeTop, INTENT_DELAY);
      });
    });

    nav.addEventListener('pointerdown', function (event) {
      pointerType = event.pointerType || 'mouse';
      const item = event.target.closest('.dropdown__menuitem--nested');
      wasOpenOnPress = Boolean(item && item.classList.contains(OPEN_CLASS));
    }, true);

    // Runs before the theme's double-tap handler on the top-level item, which
    // would otherwise swallow taps on links inside the dropdown.
    nav.addEventListener('click', function (event) {
      const link = event.target.closest('.dropdown a');
      if (!link) return;
      event.stopPropagation();

      const item = link.parentElement;
      if (pointerType === 'mouse' || !item.classList.contains('dropdown__menuitem--nested')) return;
      if (!wasOpenOnPress) {
        event.preventDefault();
        open(item);
      }
    }, true);

    document.addEventListener('pointerdown', function (event) {
      if (!nav.contains(event.target)) closeTop();
    });

    window.addEventListener('resize', closeTop);
  }

  function init() {
    document.querySelectorAll('.js-navigation').forEach(setUp);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
